import { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Divider,
} from '@mui/material';
import {
  QuestionAnswer as QuestionsIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';
import AtsInterviewQuestionsDialog from './AtsInterviewQuestionsDialog';

/**
 * Preguntas de entrevista en la ficha del candidato (drawer).
 *
 * Las preguntas son del CARGO: se definen (con IA o a mano) solo desde el
 * modal de entrevistas (botón "Entrevistas" de la columna del kanban). Aquí se
 * ANOTAN las respuestas de este candidato, compartidas por el equipo de la
 * vacante. Si una pregunta se elimina del cargo, su respuesta sigue visible en
 * solo lectura. Entrada compacta a propósito: el drawer es angosto y el
 * trabajo real se hace en el modal.
 *
 * @param {boolean} inInterviewStage - El candidato está en una etapa con el
 *   módulo de entrevista y el proceso es accionable: solo entonces se ofrece
 *   anotar (o se avisa que el cargo aún no tiene preguntas).
 * @param {boolean} canEdit - Permite editar respuestas. Lo ya anotado sigue
 *   accesible aunque el candidato avance o se descarte.
 * @param {string} questionsKey - Cambia cuando cambian las preguntas del cargo
 *   (p. ej. se definieron desde el modal de entrevistas): fuerza recarga.
 */
const AtsInterviewQuestions = ({
  applicationId, candidateName = '', inInterviewStage = false, canEdit = true, open = true,
  questionsKey = '',
}) => {
  const { t } = useTranslation();
  const { authToken } = useStore.getState();

  const [questions, setQuestions] = useState([]);
  const [removedQuestions, setRemovedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const fetchQuestions = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(
        `/applications/${applicationId}/interview-questions`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      const view = data.response?.data?.interviewQuestions || null;
      setQuestions(view?.questions || []);
      setRemovedQuestions(view?.removedQuestions || []);
    } catch (err) {
      console.error('Error cargando preguntas de entrevista:', err);
      setQuestions([]);
      setRemovedQuestions([]);
    } finally {
      setLoading(false);
    }
  }, [applicationId, authToken]);

  // Al abrir o al cambiar de candidato: cerrar el modal que hubiera quedado abierto.
  useEffect(() => {
    if (open) setDialogOpen(false);
  }, [open, applicationId]);

  // Cargar al abrir, al cambiar de candidato o si cambian las preguntas del cargo.
  useEffect(() => {
    if (!open || !applicationId) return;
    fetchQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, applicationId, questionsKey]);

  const total = questions.length;
  const answered = questions.filter((q) => (q.answer || '').trim()).length;
  const hasRemoved = removedQuestions.length > 0;

  // Mismo criterio de visibilidad que antes: se muestra si hay algo que ver o
  // hacer. Anotar se ofrece en etapa de entrevista; lo ya anotado se ve siempre.
  const showAnswers = (total > 0 && (inInterviewStage || answered > 0)) || hasRemoved;
  const showMissingHint = total === 0 && inInterviewStage;

  if (loading && !inInterviewStage && total === 0 && !hasRemoved) return null;
  if (!loading && !showAnswers && !showMissingHint) return null;

  const buttonLabel = total === 0
    ? t('ats.interviewQuestions.viewAnswersPlain')
    : canEdit
      ? t('ats.interviewQuestions.answer', { answered, total })
      : t('ats.interviewQuestions.viewAnswers', { answered, total });

  return (
    <>
      <Divider sx={{ mb: 2 }} />
      <Box sx={{ mb: 3 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
          {t('ats.interviewQuestions.title')}
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1.5 }}>
            <CircularProgress size={18} />
          </Box>
        ) : (
          <>
            {showAnswers && (
              <Button
                size="small"
                variant="outlined"
                fullWidth
                onClick={() => setDialogOpen(true)}
                startIcon={<QuestionsIcon sx={{ fontSize: 16 }} />}
                sx={{
                  fontSize: '0.75rem',
                  borderColor: '#7C3AED',
                  color: '#7C3AED',
                  '&:hover': { borderColor: '#6D28D9', backgroundColor: '#F5F3FF' },
                }}
              >
                {buttonLabel}
              </Button>
            )}

            {/* El cargo aún no tiene preguntas: indicar dónde se definen */}
            {showMissingHint && (
              <Typography
                variant="caption"
                color="text.disabled"
                sx={{ display: 'block', mt: showAnswers ? 0.75 : 0, fontSize: '0.68rem', lineHeight: 1.4 }}
              >
                {t('ats.interviewQuestions.missingPositionQuestions')}
              </Typography>
            )}
          </>
        )}
      </Box>

      {dialogOpen && (
        <AtsInterviewQuestionsDialog
          open
          onClose={() => setDialogOpen(false)}
          applicationId={applicationId}
          candidateName={candidateName}
          questions={questions}
          removedQuestions={removedQuestions}
          canEdit={canEdit}
          onChange={setQuestions}
        />
      )}
    </>
  );
};

export default AtsInterviewQuestions;
